import { useLayoutEffect, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * A ROLAGEM NA TROCA DE ROTA (26/10/2026, pedido do pesquisador). O `BrowserRouter` não mexe na rolagem: a página nova
 * abria na altura em que a anterior estava (da turma rolada até 983 px, o aluno abria em 490).
 *
 *   - ir para outra página (PUSH ou REPLACE, com o CAMINHO diferente) → o topo. Só a querystring mudando (filtro, aba no
 *     endereço) não rola: é a mesma página. Com âncora (`#simulado-…`) também não: quem a lê rola até ela.
 *   - voltar ou avançar pelo navegador (POP) → a posição em que aquela entrada estava. A página costuma desenhar depois
 *     (carregamento, Suspense), então tenta a cada quadro, até caber ou até 3 s — e desiste se a pessoa rolar antes.
 *
 * A posição de cada entrada fica guardada pela `location.key`, na sessão do navegador (sobrevive a recarregar a página).
 * `history.scrollRestoration = 'manual'`: a restauração do navegador corria antes de a página ter altura e caía no meio.
 * A volta da correção para a lista de simulados tem a sua (`AvailableSimulationsList`), e roda depois desta.
 */
const CHAVE = 'rolagem-por-entrada';

function lerTodas(): Record<string, number> {
  try { return JSON.parse(sessionStorage.getItem(CHAVE) ?? '{}') as Record<string, number>; } catch { return {}; }
}
function guardar(key: string, y: number) {
  try {
    const todas = lerTodas();
    todas[key] = Math.round(y);
    const chaves = Object.keys(todas);
    // Só as 50 entradas mais recentes.
    for (const k of chaves.slice(0, Math.max(0, chaves.length - 50))) delete todas[k];
    sessionStorage.setItem(CHAVE, JSON.stringify(todas));
  } catch { /* sem armazenamento: só não restaura */ }
}

export function RolagemDaRota() {
  const local = useLocation();
  const tipo = useNavigationType();
  const anterior = useRef<{ key: string; pathname: string } | null>(null);

  useEffect(() => {
    try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch { /* nada */ }
  }, []);

  // A posição da entrada ATUAL, guardada enquanto se rola. A chave vem de `atual`, trocada já na troca de rota: o evento de
  // rolagem que a própria troca provoca (o topo, ou a página nova mais curta) chega depois e cai na entrada nova — nunca
  // sobrescreve a posição da que ficou para trás.
  const atual = useRef(local.key);
  useEffect(() => {
    let quadro = 0;
    const aoRolar = () => { cancelAnimationFrame(quadro); quadro = requestAnimationFrame(() => guardar(atual.current, window.scrollY)); };
    window.addEventListener('scroll', aoRolar, { passive: true });
    return () => { cancelAnimationFrame(quadro); window.removeEventListener('scroll', aoRolar); };
  }, []);

  useLayoutEffect(() => {
    const antes = anterior.current;
    anterior.current = { key: local.key, pathname: local.pathname };
    atual.current = local.key;
    if (!antes || antes.key === local.key) return;
    if (tipo === 'POP') {
      const y = lerTodas()[local.key];
      if (!y) { window.scrollTo(0, 0); return; }
      const ate = performance.now() + 3000;
      let parar = false;
      const desistir = () => { parar = true; };
      window.addEventListener('wheel', desistir, { once: true, passive: true });
      window.addEventListener('touchstart', desistir, { once: true, passive: true });
      window.addEventListener('keydown', desistir, { once: true });
      const tentar = () => {
        if (parar) return;
        const cabe = document.documentElement.scrollHeight - window.innerHeight >= y - 1;
        window.scrollTo(0, y);
        if (!cabe && performance.now() < ate) requestAnimationFrame(tentar);
        else { window.removeEventListener('wheel', desistir); window.removeEventListener('touchstart', desistir); window.removeEventListener('keydown', desistir); }
      };
      tentar();
      return;
    }
    if (antes.pathname === local.pathname || local.hash) return;
    window.scrollTo(0, 0);
  }, [local.key, local.pathname, local.hash, tipo]);

  return null;
}
